import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import {
  getApiV5FirmsContactsRecipientAccounts,
  type AccountIndexSchema,
  type ContactShowSchema,
} from '@liscio/api';
import {
  EMPLOYEES_GROUP_ACCOUNT_ID,
  GroupedContactAutocomplete,
  type AccountGroupAccount,
  type ContactOption,
  isRecipientRemovable,
  type SelectedChipData,
  type SelectionKey,
} from '@liscio/common';
import { Box } from '@liscio/ui';

import { InlineCreateAccountPanel } from './InlineCreateAccountPanel';
import {
  InlineCreateContactPanel,
  type ExistingContactSelection,
} from './InlineCreateContactPanel';
import {
  isEmployeesGroupAccountId,
  mapToAccountGroups,
} from './mapToAccountGroups';
import { useFirmsContactsGroupedByAccount } from '../../fetch-utils/contacts/contacts-v5-hooks';
import { useProfile } from '../../fetch-utils/users/user-hooks';
import { ContactCardPopover } from '../ContactCard';

export interface SelectedContact {
  id: number | string;
  uuid: string;
  cpa_user_id?: number;
  contact_id?: number | string;
  first_name: string;
  last_name: string;
  middle_name?: string | null;
  email: string;
  avatar?: string | null;
  type: 'contact' | 'employee' | 'user';
  selectedAccountId?: number;
  account_id?: number;
  account_name?: string;
  is_recipient?: boolean;
  canRemove?: boolean;
  canAssignAccount?: boolean;
  message_participant_id?: number;
}

export type SelectionMode = 'single' | 'multi';

export interface AccountContactSelectorProps {
  value?: SelectedContact[];
  onChange: (contacts: SelectedContact[]) => void;
  label?: string;
  /** Keep `label` as the accessible name but do not render it. */
  hideLabel?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  maxWidth?: string | number;
  selectionMode?: SelectionMode;
  multiAccount?: boolean;
  includeEmployees?: boolean;
  includeAccountlessContacts?: boolean;
  excludeCurrentEmployee?: boolean;
  accountRequired?: boolean;
  contactRequired?: boolean;
  allowCreateContact?: boolean;
  allowCreateAccount?: boolean;
  showContactCard?: boolean;
  showContactCardNavigationLinks?: boolean;
  onInteract?: () => void;
  fixedAccountId?: number;
  maxSelections?: number;
  dropdownPlacement?: 'top' | 'bottom';
}

const getInitials = (firstName: string, lastName: string): string => {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return (first + last).toUpperCase();
};

function contactToSelectedContact(
  contact: ContactOption,
  account: AccountGroupAccount
): SelectedContact {
  const nameParts = contact.name.split(' ');
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ');

  const isEmployee = contact.type === 'employee';
  const isEmployeesGroup = isEmployeesGroupAccountId(account.id);
  const isAccountlessGroup = account.isAccountlessGroup === true;

  const base: SelectedContact = {
    id: contact.id,
    uuid: contact.uuid,
    cpa_user_id: contact.cpa_user_id ?? undefined,
    contact_id: contact.contact_id,
    first_name: firstName,
    last_name: lastName,
    email: contact.email ?? '',
    avatar: contact.avatar,
    type: isEmployee ? 'employee' : 'contact',
  };

  if (isAccountlessGroup) {
    return { ...base, canAssignAccount: false };
  }

  if (isEmployee && isEmployeesGroup) {
    return base;
  }

  return {
    ...base,
    selectedAccountId: account.id,
    account_id: account.id,
    account_name: account.name,
  };
}

function participantMissingAccount(contact: SelectedContact): boolean {
  return !contact.account_id && !contact.selectedAccountId;
}

function selectionContactId(contact: SelectedContact): number | string {
  if (contact.type === 'user') {
    return `user:${contact.cpa_user_id ?? contact.id}`;
  }

  return contact.type === 'contact' && participantMissingAccount(contact)
    ? contact.contact_id ?? contact.id
    : contact.id;
}

function isSameContactIdentity(
  selected: SelectedContact,
  contactId: number | string
): boolean {
  return (
    selected.type === 'contact' &&
    String(selected.contact_id ?? selected.id) === String(contactId)
  );
}

function isSameAccountlessContact(
  selected: SelectedContact,
  contactId: number | string
): boolean {
  return (
    isSameContactIdentity(selected, contactId) &&
    participantMissingAccount(selected)
  );
}

function isSameMembershipIdentity(
  selected: SelectedContact,
  membershipId: number | string
): boolean {
  return (
    selected.type === 'user' &&
    selectionContactId(selected) === String(membershipId)
  );
}

function isSameAccountlessMembership(
  selected: SelectedContact,
  membershipId: number | string
): boolean {
  return (
    participantMissingAccount(selected) &&
    isSameMembershipIdentity(selected, membershipId)
  );
}

export function isSameRecipientIdentity(
  selected: SelectedContact,
  key: SelectionKey
): boolean {
  if (selected.type === 'user') {
    return isSameMembershipIdentity(selected, key.contactId);
  }

  if (isEmployeesGroupAccountId(key.accountId)) {
    return (
      selected.type === 'employee' &&
      String(selected.id) === String(key.contactId)
    );
  }

  return key.accountId === 0
    ? isSameContactIdentity(selected, key.contactId) ||
        isSameAccountlessMembership(selected, key.contactId)
    : String(selected.id) === String(key.contactId);
}

function recipientAccountLookup(contact: SelectedContact) {
  if (contact.canAssignAccount === false) return;

  if (contact.cpa_user_id != null) {
    return {
      key: `user:${contact.cpa_user_id}`,
      params: { recipient_cpa_user_id: contact.cpa_user_id },
    };
  }

  const contactId = Number(contact.contact_id);
  if (
    contact.type !== 'contact' ||
    !Number.isSafeInteger(contactId) ||
    contactId <= 0
  ) {
    return;
  }

  return { key: `contact:${contactId}`, params: { contact_id: contactId } };
}

function createdContactToSelectedContact(
  created: ContactShowSchema,
  account: AccountGroupAccount
): SelectedContact {
  return {
    id: created.id,
    uuid: created.uuid,
    contact_id: created.id,
    first_name: created.first_name ?? '',
    last_name: created.last_name,
    middle_name: created.middle_name,
    email: created.primary_email ?? '',
    type: 'contact',
    selectedAccountId: account.id,
    account_id: account.id,
    account_name: account.name,
  };
}

function existingContactToSelectedContact(
  selection: ExistingContactSelection
): SelectedContact {
  return {
    id: selection.contact_id,
    uuid: selection.contact_uuid,
    cpa_user_id: selection.cpa_user_id ?? undefined,
    contact_id: selection.contact_id,
    first_name: selection.first_name ?? '',
    last_name: selection.last_name ?? '',
    email: selection.email,
    type: 'contact',
    selectedAccountId: selection.account_id,
    account_id: selection.account_id,
    account_name: selection.account_name,
  };
}

export const AccountContactSelector: React.FC<AccountContactSelectorProps> = ({
  value = [],
  onChange,
  label,
  hideLabel = false,
  placeholder = 'Search contacts & accounts',
  error = false,
  helperText,
  disabled = false,
  maxWidth,
  selectionMode = 'multi',
  multiAccount = true,
  includeEmployees = true,
  includeAccountlessContacts = false,
  excludeCurrentEmployee = false,
  accountRequired = false,
  contactRequired = false,
  allowCreateContact = true,
  allowCreateAccount = true,
  showContactCard = true,
  showContactCardNavigationLinks = true,
  onInteract,
  fixedAccountId,
  maxSelections,
  dropdownPlacement,
}) => {
  const queryClient = useQueryClient();
  const effectiveIncludeEmployees = includeEmployees && fixedAccountId == null;
  const effectiveIncludeAccountless =
    includeAccountlessContacts && fixedAccountId == null;
  const profileRequired = effectiveIncludeEmployees && excludeCurrentEmployee;
  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useProfile(profileRequired);
  const currentCpaUserId = profile?.cpa_user_id;
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKeyword(searchKeyword), 250);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const {
    data,
    isLoading,
    isFetching,
    isError: isRecipientSearchError,
  } = useFirmsContactsGroupedByAccount(
    debouncedKeyword,
    true,
    'search',
    fixedAccountId,
    effectiveIncludeAccountless
  );
  const isDebouncing = searchKeyword.trim() !== debouncedKeyword.trim();
  const [accountOptionsByRecipient, setAccountOptionsByRecipient] = useState<
    Record<string, AccountGroupAccount[]>
  >({});
  const [accountEligibleRecipients, setAccountEligibleRecipients] = useState<
    Set<string>
  >(new Set());
  const accountOptionsRequestedRef = useRef(new Set<string>());
  const accountSearchSequenceRef = useRef(new Map<string, number>());

  const visibleEmployees = useMemo(() => {
    if (!effectiveIncludeEmployees) return [];
    if (!excludeCurrentEmployee) return data?.employees ?? [];
    if (currentCpaUserId == null) return [];

    return (data?.employees ?? []).filter(
      (employee) => employee.cpa_user_id !== currentCpaUserId
    );
  }, [
    currentCpaUserId,
    data?.employees,
    excludeCurrentEmployee,
    effectiveIncludeEmployees,
  ]);

  const [openCreateContactForAccount, setOpenCreateContactForAccount] =
    useState<AccountGroupAccount | null>(null);

  const visibleAccounts = useMemo(
    () =>
      fixedAccountId == null
        ? data?.accounts ?? []
        : (data?.accounts ?? []).filter(
            (account) => account.id === fixedAccountId
          ),
    [data?.accounts, fixedAccountId]
  );
  const groups = useMemo(
    () =>
      mapToAccountGroups(visibleAccounts, visibleEmployees, {
        includeEmployees: effectiveIncludeEmployees,
        accountlessContacts: effectiveIncludeAccountless
          ? data?.accountless_contacts
          : [],
      }),
    [
      data?.accountless_contacts,
      effectiveIncludeAccountless,
      effectiveIncludeEmployees,
      visibleAccounts,
      visibleEmployees,
    ]
  );
  const hasAvailableResults = groups.some((group) => group.contacts.length > 0);
  const isUpdatingResults = hasAvailableResults && (isFetching || isDebouncing);

  const multiSelect = selectionMode === 'multi';
  const profileUnavailable =
    profileRequired &&
    !isProfileLoading &&
    (isProfileError || currentCpaUserId == null);

  const requirementValidation = useMemo(() => {
    const hasAccounts =
      (data?.accounts?.length ?? 0) > 0 ||
      value.some(
        (contact) =>
          contact.account_id != null || contact.selectedAccountId != null
      );
    const isEmployeeOnlySelection =
      value.length > 0 &&
      value.every(
        (contact) =>
          contact.type === 'employee' && participantMissingAccount(contact)
      );

    const accountRequiredViolation =
      accountRequired &&
      hasAccounts &&
      value.length > 0 &&
      value.some(participantMissingAccount) &&
      !(effectiveIncludeEmployees && isEmployeeOnlySelection);

    const contactRequiredViolation = contactRequired && value.length === 0;

    return {
      hasError: accountRequiredViolation || contactRequiredViolation,
      helperText: accountRequiredViolation
        ? 'All participants must be associated with an account, unless the user has no accounts'
        : contactRequiredViolation
        ? 'At least one contact is required'
        : undefined,
    };
  }, [
    accountRequired,
    contactRequired,
    data?.accounts,
    value,
    effectiveIncludeEmployees,
  ]);

  const resolvedError =
    error ||
    requirementValidation.hasError ||
    profileUnavailable ||
    (fixedAccountId != null && isRecipientSearchError);
  const resolvedHelperText =
    helperText ??
    requirementValidation.helperText ??
    (profileUnavailable
      ? 'Unable to load your profile. Refresh and try again.'
      : fixedAccountId != null && isRecipientSearchError
      ? 'Unable to load Contacts for this Account. Return to the Account and try again.'
      : isUpdatingResults
      ? 'Updating results...'
      : undefined);

  const invalidateContactsQuery = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['firmsContactsGroupedByAccount'],
    });
  }, [queryClient]);

  const selectedKeys: SelectionKey[] = useMemo(
    () =>
      value.map((contact) => ({
        accountId:
          contact.account_id ??
          contact.selectedAccountId ??
          (contact.type === 'employee' ? EMPLOYEES_GROUP_ACCOUNT_ID : 0),
        contactId: selectionContactId(contact),
      })),
    [value]
  );

  const selectedChips: SelectedChipData[] = useMemo(
    () =>
      value.flatMap((contact) => {
        const fullName =
          `${contact.first_name} ${contact.last_name}`.trim() || contact.email;
        const labelText = fullName;
        if (!labelText.trim()) return [];

        const lookup =
          contact.canAssignAccount === false
            ? undefined
            : recipientAccountLookup(contact);
        const canRemove = isRecipientRemovable(contact);
        const accountOptionsLoaded =
          lookup != null &&
          Object.prototype.hasOwnProperty.call(
            accountOptionsByRecipient,
            lookup.key
          );

        return [
          {
            key: {
              accountId:
                contact.account_id ??
                contact.selectedAccountId ??
                (contact.type === 'employee' ? EMPLOYEES_GROUP_ACCOUNT_ID : 0),
              contactId: selectionContactId(contact),
            },
            label: labelText,
            canRemove,
            initials: getInitials(contact.first_name, contact.last_name),
            avatar: contact.avatar,
            contactId: contact.contact_id ?? contact.id,
            accountName: contact.account_name,
            accountOptions:
              canRemove && participantMissingAccount(contact) && lookup
                ? accountOptionsByRecipient[lookup.key]
                : undefined,
            canAssignAccount:
              contact.canAssignAccount === false || !canRemove || lookup == null
                ? false
                : accountOptionsLoaded
                ? accountEligibleRecipients.has(lookup.key)
                : undefined,
            isFirmEmployee: contact.type === 'employee',
          },
        ];
      }),
    [accountEligibleRecipients, accountOptionsByRecipient, value]
  );

  const isContactSelected = useCallback(
    (contactId: number | string, accountId?: number) => {
      return value.some((c) => {
        if (accountId === 0) {
          return isSameAccountlessContact(c, contactId);
        }
        if (isEmployeesGroupAccountId(accountId ?? 0)) {
          return c.id === contactId && c.type === 'employee' && !c.account_id;
        }
        return c.id === contactId && c.account_id === accountId;
      });
    },
    [value]
  );

  const handleSelect = useCallback(
    (account: AccountGroupAccount, contact: ContactOption) => {
      const alreadySelected = isContactSelected(contact.id, account.id);

      if (alreadySelected) {
        onChange(
          value.filter((c) => {
            if (account.isAccountlessGroup) {
              return !isSameAccountlessContact(c, contact.id);
            }
            if (contact.type === 'employee' && account.isEmployeesGroup) {
              return !(
                c.id === contact.id &&
                c.type === 'employee' &&
                !c.account_id
              );
            }
            return !(c.id === contact.id && c.account_id === account.id);
          })
        );
        return;
      }

      if (!multiSelect) {
        onChange([contactToSelectedContact(contact, account)]);
        return;
      }

      if (maxSelections != null && value.length >= maxSelections) {
        toast.error(`You can select up to ${maxSelections} recipients.`);
        return;
      }

      onChange([...value, contactToSelectedContact(contact, account)]);
    },
    [isContactSelected, maxSelections, multiSelect, onChange, value]
  );

  const handleRemoveChip = useCallback(
    (key: SelectionKey) => {
      onChange(
        value.filter((c) => {
          if (isEmployeesGroupAccountId(key.accountId)) {
            return !(
              c.id === key.contactId &&
              c.type === 'employee' &&
              !c.account_id
            );
          }
          if (key.accountId === 0) {
            return !(
              isSameAccountlessContact(c, key.contactId) ||
              isSameAccountlessMembership(c, key.contactId)
            );
          }
          return !(
            isSameRecipientIdentity(c, key) &&
            (c.account_id ?? c.selectedAccountId) === key.accountId
          );
        })
      );
    },
    [onChange, value]
  );

  const handleRemoveAccount = useCallback(
    (accountId: number) => {
      onChange(
        value.map((contact) =>
          (contact.account_id === accountId ||
            contact.selectedAccountId === accountId) &&
          isRecipientRemovable(contact)
            ? {
                ...contact,
                account_id: undefined,
                selectedAccountId: undefined,
                account_name: undefined,
              }
            : contact
        )
      );
    },
    [onChange, value]
  );

  const handleAssignAccount = useCallback(
    (key: SelectionKey, account: AccountGroupAccount) => {
      const target = value.find(
        (contact) =>
          isSameRecipientIdentity(contact, key) &&
          participantMissingAccount(contact)
      );
      if (target && !isRecipientRemovable(target)) return;

      const targetExists = value.some(
        (contact) =>
          isSameRecipientIdentity(contact, key) &&
          (contact.account_id ?? contact.selectedAccountId) === account.id
      );

      onChange(
        targetExists
          ? value.filter(
              (contact) =>
                !(
                  isSameRecipientIdentity(contact, key) &&
                  participantMissingAccount(contact)
                )
            )
          : value.map((contact) =>
              isSameRecipientIdentity(contact, key) &&
              participantMissingAccount(contact)
                ? {
                    ...contact,
                    account_id: account.id,
                    selectedAccountId: account.id,
                    account_name: account.name,
                  }
                : contact
            )
      );
    },
    [onChange, value]
  );

  const fetchRecipientAccountOptions = useCallback(
    async (key: SelectionKey, keyword = '') => {
      const recipient = value.find(
        (contact) =>
          isSameRecipientIdentity(contact, key) &&
          participantMissingAccount(contact)
      );
      if (!recipient) return [];
      const lookup = recipientAccountLookup(recipient);
      if (!lookup) return [];
      const requestSequence =
        (accountSearchSequenceRef.current.get(lookup.key) ?? 0) + 1;
      accountSearchSequenceRef.current.set(lookup.key, requestSequence);

      const response = await getApiV5FirmsContactsRecipientAccounts({
        ...lookup.params,
        keyword,
      });
      const options = (response.data ?? []).map((account) => ({
        id: account.id,
        name: account.name,
        uuid: String(account.id),
      }));
      if (
        accountSearchSequenceRef.current.get(lookup.key) !== requestSequence
      ) {
        return options;
      }

      setAccountOptionsByRecipient((current) => ({
        ...current,
        [lookup.key]: options,
      }));
      if (!keyword) {
        setAccountEligibleRecipients((current) => {
          const next = new Set(current);
          if (options.length > 0) next.add(lookup.key);
          else next.delete(lookup.key);
          return next;
        });
      }
      return options;
    },
    [value]
  );

  const handleRefreshAccountOptions = useCallback(
    async (key: SelectionKey, keyword = '') => {
      try {
        await fetchRecipientAccountOptions(key, keyword);
      } catch (error) {
        toast.error('Unable to load Account options. Please try again.');
        throw error;
      }
    },
    [fetchRecipientAccountOptions]
  );

  useEffect(() => {
    value.forEach((contact) => {
      const lookup = recipientAccountLookup(contact);
      if (
        !participantMissingAccount(contact) ||
        !isRecipientRemovable(contact) ||
        !lookup ||
        Object.prototype.hasOwnProperty.call(
          accountOptionsByRecipient,
          lookup.key
        ) ||
        accountOptionsRequestedRef.current.has(lookup.key)
      ) {
        return;
      }

      accountOptionsRequestedRef.current.add(lookup.key);
      void fetchRecipientAccountOptions({
        accountId: 0,
        contactId: selectionContactId(contact),
      })
        .catch(() => undefined)
        .finally(() => {
          accountOptionsRequestedRef.current.delete(lookup.key);
        });
    });
  }, [accountOptionsByRecipient, fetchRecipientAccountOptions, value]);

  const handleCreateContactSuccess = useCallback(
    async (created: ContactShowSchema, account: AccountGroupAccount) => {
      onChange([...value, createdContactToSelectedContact(created, account)]);
      await invalidateContactsQuery();
    },
    [invalidateContactsQuery, onChange, value]
  );

  const handleSelectExistingContact = useCallback(
    async (selection: ExistingContactSelection) => {
      const alreadySelected = value.some(
        (contact) =>
          Number(contact.contact_id ?? contact.id) === selection.contact_id &&
          contact.account_id === selection.account_id
      );

      if (alreadySelected) return;

      onChange([...value, existingContactToSelectedContact(selection)]);
      await invalidateContactsQuery();
    },
    [invalidateContactsQuery, onChange, value]
  );

  const handleCreateAccountSuccess = useCallback(
    async (created: AccountIndexSchema) => {
      await invalidateContactsQuery();

      if (created.id != null) {
        setOpenCreateContactForAccount({
          id: created.id,
          uuid: created.uuid,
          name: created.name,
        });
      }
    },
    [invalidateContactsQuery]
  );

  const getOptionDisabled = useCallback(
    (account: AccountGroupAccount) => {
      if (maxSelections != null && value.length >= maxSelections) return true;
      if (multiAccount) return false;
      if (value.length === 0) return false;
      const firstAccountId = value[0]?.account_id;
      if (firstAccountId == null) return false;
      return account.id !== firstAccountId && !account.isEmployeesGroup;
    },
    [maxSelections, multiAccount, value]
  );

  const renderChip = useCallback(
    (chip: SelectedChipData, chipElement: React.ReactElement) => {
      const contactId = chip.contactId;

      if (contactId && showContactCard) {
        return (
          <ContactCardPopover
            contactId={contactId}
            showNavigationLinks={showContactCardNavigationLinks}
          >
            {chipElement}
          </ContactCardPopover>
        );
      }

      return chipElement;
    },
    [showContactCard, showContactCardNavigationLinks]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: maxWidth ?? '100%' }}>
      {label && !hideLabel && (
        <Box
          sx={{
            mb: 1,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          {label}
        </Box>
      )}
      <GroupedContactAutocomplete
        ariaLabel={label}
        dropdownPlacement={dropdownPlacement}
        compact
        groups={groups}
        loading={
          isLoading ||
          (!hasAvailableResults && (isFetching || isDebouncing)) ||
          (profileRequired && isProfileLoading)
        }
        searchQuery={searchKeyword}
        onSearchChange={(keyword) => {
          onInteract?.();
          setSearchKeyword(keyword);
        }}
        onInteract={onInteract}
        multiSelect={multiSelect}
        selectedKeys={selectedKeys}
        selectedChips={selectedChips}
        onSelect={handleSelect}
        onRemoveChip={handleRemoveChip}
        onRemoveAccount={
          fixedAccountId == null ? handleRemoveAccount : undefined
        }
        onAssignAccount={
          fixedAccountId == null ? handleAssignAccount : undefined
        }
        onRefreshAccountOptions={
          fixedAccountId == null ? handleRefreshAccountOptions : undefined
        }
        getOptionDisabled={getOptionDisabled}
        allowCreateContact={allowCreateContact}
        allowCreateAccount={
          fixedAccountId == null &&
          allowCreateAccount &&
          searchKeyword.trim().length >= 2
        }
        minSearchLength={0}
        placeholder={placeholder}
        disabled={disabled}
        error={resolvedError}
        helperText={resolvedHelperText}
        renderChip={renderChip}
        openCreateContactForAccount={openCreateContactForAccount}
        onOpenCreateContactForAccountHandled={() =>
          setOpenCreateContactForAccount(null)
        }
        renderCreateContactPanel={({ account, onBack, onCancel }) => (
          <InlineCreateContactPanel
            account={account}
            fixedAccountId={fixedAccountId}
            onBack={onBack}
            onCancel={onCancel}
            onSuccess={(created) => {
              handleCreateContactSuccess(created, account);
              onBack();
            }}
            onSelectExistingContact={(selection) => {
              handleSelectExistingContact(selection);
              onBack();
            }}
            isExistingContactSelected={isContactSelected}
          />
        )}
        renderCreateAccountPanel={({ prefillName, onBack, onCancel }) => (
          <InlineCreateAccountPanel
            prefillName={prefillName}
            onBack={onBack}
            onCancel={onCancel}
            onSuccess={(created) => {
              handleCreateAccountSuccess(created);
              onBack();
            }}
          />
        )}
      />
    </Box>
  );
};

export type ContactWithAccount = SelectedContact;

export default AccountContactSelector;
